import React, { useEffect, useState, useRef } from 'react';
import './SimpleRotatingText.css';

interface SimpleRotatingTextProps {
  texts: string[];
  speed?: number;
  typingSpeed?: number;
  erasingSpeed?: number;
  className?: string;
}

// Helper function to split text into segments (emojis and text)
const segmentText = (text: string): string[] => {
  // Simple approach to handle common emoji patterns
  // This won't catch all emojis but covers the ones in our specific use case
  const segments: string[] = [];
  const emojiPattern = /([\uD800-\uDBFF][\uDC00-\uDFFF])/g;
  
  // Process the text character by character
  let i = 0;
  while (i < text.length) {
    const char = text.charAt(i);
    const nextChar = text.charAt(i + 1);
    
    // Check if this is a potential emoji (surrogate pair)
    if (/[\uD800-\uDBFF]/.test(char) && /[\uDC00-\uDFFF]/.test(nextChar)) {
      segments.push(char + nextChar);
      i += 2; // Skip the surrogate pair
    } else {
      segments.push(char);
      i += 1;
    }
  }
  
  return segments;
};

const SimpleRotatingText: React.FC<SimpleRotatingTextProps> = ({
  texts,
  speed = 2000,
  typingSpeed = 80,
  erasingSpeed = 50,
  className = '',
}) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [segments, setSegments] = useState<string[]>([]);
  const [displayedSegments, setDisplayedSegments] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Prepare segments when current text changes
  useEffect(() => {
    if (texts.length > 0) {
      setSegments(segmentText(texts[currentTextIndex]));
    }
  }, [texts, currentTextIndex]);

  useEffect(() => {
    if (!segments.length) return;
    if (isPaused) return;

    // Typing effect
    if (isTyping) {
      if (displayedSegments.length < segments.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedSegments(segments.slice(0, displayedSegments.length + 1));
        }, typingSpeed);
      } else {
        // Done typing, pause before erasing
        timeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, speed);
      }
    } 
    // Erasing effect
    else {
      if (displayedSegments.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedSegments(displayedSegments.slice(0, displayedSegments.length - 1));
        }, erasingSpeed);
      } else {
        // Done erasing, move to next text
        setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
        setIsTyping(true);
      }
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [segments, displayedSegments, isTyping, isPaused, speed, typingSpeed, erasingSpeed]);

  return (
    <span className={`simple-rotating-text ${className}`}>
      <span className="typing-text">
        {displayedSegments.length > 0 ? displayedSegments.join('') : '\u00A0'}
        <span className="cursor"></span>
      </span>
    </span>
  );
};

export default SimpleRotatingText; 